import { CommandHistoryQueryParams } from '@/store/hes/types/other';
import { ExecutionHistoryDetailsRecordModified } from '@/store/hes/types/records/command-execution';

type ExecutionHistoryParams = {
  query: CommandHistoryQueryParams;
  search: string;
  postFix?: string;
};

/**
 * Function to add or update tableData with PSS, Feeder, or DTR data.
 * The total_count will include only the length of the device_identifier array.
 */

export const getCommandExecutionHistoryUrlSearchParams = ({
  query,
  search,
  postFix = ''
}: ExecutionHistoryParams) => {
  const matcherKeys = [
    'command_status',
    'device_identifier',
    'event_codes',
    'device_serial'
  ];

  const { ...rest } = query;

  let newQuery = Object.entries(rest).reduce((acc, [key, value]) => {
    if (matcherKeys.includes(key)) return acc; // We'll handle this below

    if (value && value.toString().length > 0) {
      return acc === '' ? `?${key}=${value}` : `${acc}&${key}=${value}`;
    }
    return acc;
  }, search);

  matcherKeys.forEach((key) => {
    const values = query[key as keyof CommandHistoryQueryParams];
    if (Array.isArray(values) && values.length > 0) {
      values.forEach((value) => {
        if (value.length > 0) {
          newQuery =
            newQuery === ''
              ? `?${key}=${value}`
              : `${newQuery}&${key}=${value}`;
        }
      });
    }
  });

  return newQuery + (newQuery ? `&${postFix}` : `?${postFix}`);
};

export type ExecutionHistoryDetailRecordModified = {
  [key: string]: { [key: string]: number | string }[];
};

type Obj = {
  [key: string]: number | string;
};

export const groupEventDataByDataType = (
  records: ExecutionHistoryDetailsRecordModified[],
  commandName?: string
): { [key: string]: Obj[] } | Obj[] => {
  if (!records || records.length === 0) return [];
  const eventGroups: Record<string, Obj[]> = {}; // ✅ Correct initialization
  let hasEventData = false;
  const originalData: Obj[] = [];

  records.forEach((record) => {
    if (
      commandName &&
      ['EVENTS', 'ALL EVENT PULL'].includes(commandName) &&
      record.payload &&
      !record.payload.message
    ) {
      hasEventData = true;
      const payload = record.payload as Obj;
      const dataType = payload.data_type as string;

      if (dataType) {
        if (!eventGroups[dataType]) {
          eventGroups[dataType] = [];
        }
        eventGroups[dataType].push(payload);
      }
    } else if (record.pendingStatusReason) {
      originalData.push(record.pendingStatusReason);
    } else if (record.payload && !('message' in record.payload)) {
      originalData.push(record.payload as Obj);
    } else if (record && record.payload && 'message' in record.payload) {
      originalData.push({ message: String(record.payload.message) });
    }
  });

  return hasEventData ? eventGroups : originalData;
};

export function lightenColor(hex: string = '#808080', percent: number): string {
  // Helper function to convert hex to RGB
  function hexToRgb(hex: string): { r: number; g: number; b: number } {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return { r, g, b };
  }

  // Helper function to convert RGB to hex
  function rgbToHex(r: number, g: number, b: number): string {
    const toHex = (component: number) => {
      const hex = component.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  // Convert hex to RGB
  const { r, g, b } = hexToRgb(hex);

  // Lighten the RGB values by the percentage
  const newR = Math.min(Math.floor(r + (255 - r) * percent), 255);
  const newG = Math.min(Math.floor(g + (255 - g) * percent), 255);
  const newB = Math.min(Math.floor(b + (255 - b) * percent), 255);

  // Convert back to hex and return the lighter color
  return rgbToHex(newR, newG, newB);
}

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return ''; // Handle empty string case
  return str.charAt(0).toUpperCase() + str.slice(1);
};
